<template>
  <div>
    <ValidationObserver ref="coordinateForm">
      <custom-section title="Coordinar Cita">
        <div class="row">
          <div class="col">
            <info title="Municipio:">
              <ValidationProvider
                v-slot="{ errors }"
                name="municipio"
                rules="required"
              >
                <div class="row mt-2">
                  <custom-drop-down
                    id="municipality"
                    v-model="municipality"
                    class="col-3"
                    fieldname="name"
                    fieldkey="id"
                    placeholder="Seleccione"
                    :disabled="municipalityNotFound === true"
                    :error="errors[0] || municipalityNotFound"
                    :custom-error-msg="
                      municipalityNotFound === true
                        ? 'No se encontró pueblo disponible para la disciplina seleccionada (R90024).'
                        : errors[0]
                    "
                    :options="municipalities"
                    @input="loadAvailableDates"
                  />
                </div>
              </ValidationProvider>
            </info>
          </div>
        </div>
        <div class="row mt-4">
          <schedule
            ref="schedule"
            :is-schedule-invalid="isScheduleValid === false"
            :available-dates="availableDates"
            :schedules="schedules"
            :disabled="isScheduleDisabled"
            :schedule-selected="scheduleSelected"
            @date-changed="dateChanged"
            @schedule-selected="selectSchedule"
          />
        </div>
      </custom-section>
      <custom-section
        class="mt-4 mb-5"
        title="Favor verificar la información contacto"
      >
        <contact-form
          ref="contact-form"
          :contact-information="contactInfo"
          :relationships="relationships"
        />
      </custom-section>
    </ValidationObserver>
    <footer class="text-right">
      <ActionButton
        text="Cancelar"
        i="fas fa-times-circle fa-fw"
        variant="outline-danger"
        class="m-1"
        @click="
          () => {
            $emit('close');
          }
        "
      />
      <ActionButton
        text="Continuar"
        i="fas fa-arrow-alt-circle-right fa-fw"
        variant="success"
        class="m-1"
        :disabled="isSaveBtnDisabled"
        @click="coordinate"
      />
    </footer>
  </div>
</template>

<script>
import CustomDropDown from '@/components/common/CustomDropDown';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import ActionButton from 'ActionButton';
import Info from './Info.vue';
import ContactForm from './ContactForm.vue';

import CustomSection from './CustomSection.vue';
import Schedule from './Schedule.vue';
import appointmentServiceManager from '@/services/api/appointment.api.js';

import alert from '@/mixin/alert.mixin';

export default {
  components: {
    CustomDropDown,
    ActionButton,
    ValidationObserver,
    ValidationProvider,
    Info,
    CustomSection,
    Schedule,
    ContactForm,
  },
  mixins: [alert],
  props: {
    evaluation: { type: Object, require: true, default: () => ({}) },
    student: {
      type: Object,
      require: true,
      default: () => ({ age: 0, studentSieId: 0 }),
    },
    availableDates: [],
  },
  data() {
    return {
      municipalities: [],
      municipality: null,
      municipalityNotFound: null,
      registrationDate: null,
      schedules: [],
      contactInfo: {},
      relationships: [],
      isSaveBtnDisabled: false,
      isScheduleValid: null,
    };
  },
  async mounted() {
    this.getMunicipalities();
    await Promise.all([this.getRelationships(), this.getContactInfo()]);

    // set the relationship sent by the server to object { id, name } for the dropdown
    if (
      this.contactInfo.contactRelationId > 0 &&
      this.relationships.length > 0
    ) {
      this.contactInfo.contactRelation = this.relationships.find(
        (x) => x.eduespCodeId === this.contactInfo.contactRelationId
      );
    }
  },
  computed: {
    params() {
      return {
        studentSieId: this.student.studentId,
        disciplineId: this.evaluation.disciplineTypeId,
        municipalityId: this.municipality?.id,
      };
    },
    scheduleSelected() {
      return this.schedules.find((x) => x.isSelected) ?? {};
    },
    isScheduleDisabled() {
      const hasMunicipalitySelected = this.municipality?.id > 0;
      return this.municipalities.length === 0 || !hasMunicipalitySelected;
    },
  },
  methods: {
    async validateForm() {
      // validate schedule date and selected hour
      const {
        valid: isScheduleDateValid,
      } = await this.$refs.schedule.$refs.date.validate();
      const isScheduleValid =
        isScheduleDateValid && this.scheduleSelected.id > 0;
      this.isScheduleValid = isScheduleValid;
      // validate coordinate form (municipality)
      const isCondinateFormValid = await this.$refs.coordinateForm.validate();

      return isScheduleValid && isCondinateFormValid;
    },
    async coordinate() {
      const isInvalid = !(await this.validateForm());
      if (isInvalid || !this.scheduleSelected.id > 0) return;

      const params = {
        appointmentToCoordinate: {
          StudentContactInformation: {
            ...this.contactInfo,
            contactRelation: this.contactInfo.contactRelation?.eduespCodeId,
          },
          accept: true,
          clientAppointmentId: this.scheduleSelected.id,
          coordinatedByCenterId: this.evaluation.centerId,
          evaluationTypeId: this.evaluation.evaluationTypeId,
          studentReferralId: this.evaluation.studentReferralId,
          studentSieId: this.student.studentId,
        },
      };
      this.isSaveBtnDisabled = true;
      const {
        data: response,
      } = await appointmentServiceManager
        .saveAppointmentCoordinate(params, 'discussion')
        .catch((err) => {
          const errorMsg =
            err.data && err.data.message
              ? err.data.message
              : err.response.data.message;
          this.ShowToast('Error', errorMsg, 'error');
          this.isSaveBtnDisabled = false;
        });

      if (response) {
        this.evaluation.referralStatusId = response.referralStatusId;
        this.evaluation.referralStatusName = response.referralStatusName;
        this.ShowToast(
          'Proceso Exitoso',
          'La evaluación se ha coordinado exitosamente.',
          'success'
        );
        this.$emit('on-coordination-finished', response);
      }
    },
    async getRelationships() {
      const {
        data: response,
      } = await appointmentServiceManager.getRelationships();
      this.relationships = response;
    },
    selectSchedule(schedule) {
      this.schedules.forEach((x) => (x.isSelected = false));
      schedule.isSelected = true;
    },
    async dateChanged(date) {
      if (!date) {
        this.schedules = [];
        return;
      }
      const {
        data: response,
      } = await appointmentServiceManager.getAvailableScheduleOnDate({
        ...this.params,
        spaceDate: new Date(date).toJSON(),
        registrationDate: new Date(this.registrationDate).toJSON(),
      });

      this.schedules = response;
    },
    async getContactInfo() {
      const {
        data: response,
      } = await appointmentServiceManager.getContactInformation(
        this.student.studentId
      );

      // map the relation id stored in db into a separated property (contactRelationId)
      this.contactInfo = {
        ...response,
        contactRelation: null, // the relationships' dropdown expect an object, not a number
        contactRelationId: response.contactRelation,
      };
    },
    async loadAvailableDates() {
      if (!this.municipality?.id > 0) {
        this.schedules = [];
        this.$refs.schedule.date = null;
        return;
      }

      const {
        data: response,
      } = await appointmentServiceManager.getDatesWithAvailableSchedules(
        this.params
      );
      if (response && response.length > 0) {
        this.registrationDate = response[0].registrationDate;
        this.availableDates = response.map((x) => x.spaceDate);
      }
    },
    async getMunicipalities() {
      if (this.municipalities > 0) return;

      const params = {
        // JSF: endpoint has a typo with the discipline word.
        diciplineType: this.evaluation.disciplineTypeId,
        studentAge: this.student.age,
      };
      const {
        data: response,
      } = await appointmentServiceManager.getMunicipalitiesByDiscipline(params);
      this.municipalities = response;

      if (response.length === 0) {
        this.municipalityNotFound = true;
      }
    },
  },
};
</script>

<style scope>
.schedule-error {
  border: 1px solid #ff0018;
}
</style>
